import API_BASE_URL from "../RestAPI";

/**
 * API layer for What's New — the release notes the support team publishes to
 * clients.
 *
 * Same shape as customerSupportApi.js. Note the direction is the opposite of
 * that module: customer support *reads* what eMedHub produced, this *writes*
 * into every eMedHub deployment. Which means every call here can have a
 * partial outcome — see `publishRelease`.
 */

const BASE = `${API_BASE_URL.replace(/\/$/, "")}/release-notes`;
const TICKETING = `${API_BASE_URL.replace(/\/$/, "")}/ticketing`;
const CORE = `${API_BASE_URL.replace(/\/$/, "")}/core`;

const getAuthHeaders = () => {
    const token = localStorage.getItem("authToken");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
};

const buildQuery = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
        if (val == null || val === "") return;
        qs.append(key, String(val));
    });
    const s = qs.toString();
    return s ? `?${s}` : "";
};

const handle = async (res, fallbackMsg) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.message || res.statusText || fallbackMsg);
        // Carried through so callers can show per-blocker detail rather than
        // one flat string — the publish endpoint returns a `blockers` array.
        err.payload = data;
        throw err;
    }
    return data;
};

export const whatsNewApi = {
    // ── Organisation directory (targeting) ──────────────
    getOrgs: async (params = {}) => {
        // { server, q, client, reachable }
        const res = await fetch(`${BASE}/orgs/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to load organizations");
    },
    syncOrgs: async (server) => {
        const res = await fetch(`${BASE}/orgs/sync/`, {
            method: "POST", headers: getAuthHeaders(),
            body: JSON.stringify(server ? { server } : {}),
        });
        return handle(res, "Failed to sync organizations");
    },

    // ── Sprint tickets that may be announced ────────────
    getShareableTickets: async (sprintId) => {
        const res = await fetch(`${BASE}/shareable-tickets/${buildQuery({ sprint: sprintId })}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to load sprint tickets");
    },

    // Projects and sprints come from the existing APIs — no reason to duplicate
    // them. The project list is needed here because a release can be prepared
    // without ever visiting the sprint board, so the shared project selection
    // may well be empty.
    getProjects: async () => {
        const res = await fetch(`${CORE}/project/`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to load projects");
    },
    getSprints: async (projectId) => {
        const res = await fetch(`${TICKETING}/sprint/${buildQuery({ project_id: projectId })}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to load sprints");
    },

    // ── Releases ────────────────────────────────────────
    getReleases: async (params = {}) => {
        const res = await fetch(`${BASE}/releases/${buildQuery(params)}`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to load releases");
    },
    getRelease: async (id) => {
        const res = await fetch(`${BASE}/releases/${id}/`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to load release");
    },
    createRelease: async (payload) => {
        // { title, release_date, servers: [{server_code, version_label}] }
        const res = await fetch(`${BASE}/releases/`, {
            method: "POST", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to create release");
    },
    updateRelease: async (id, payload) => {
        const res = await fetch(`${BASE}/releases/${id}/`, {
            method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to update release");
    },
    deleteRelease: async (id, { retract = false } = {}) => {
        // `retract` withdraws a published release from every deployment before
        // deleting it. Without it the backend refuses, rather than leaving the
        // release on customers' screens with nothing left to retract it with.
        const res = await fetch(
            `${BASE}/releases/${id}/${retract ? "?retract=1" : ""}`,
            { method: "DELETE", headers: getAuthHeaders() },
        );
        return handle(res, "Failed to delete release");
    },

    // ── Updates inside a release ────────────────────────
    addItems: async (releaseId, ticketIds) => {
        const res = await fetch(`${BASE}/releases/${releaseId}/items/`, {
            method: "POST", headers: getAuthHeaders(),
            body: JSON.stringify({ ticket_ids: ticketIds }),
        });
        return handle(res, "Failed to add updates");
    },
    updateItem: async (itemId, payload) => {
        // { client_text, steps, category, audience, orgs: [{server_code, org_id}] }
        const res = await fetch(`${BASE}/items/${itemId}/`, {
            method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(payload),
        });
        return handle(res, "Failed to save update");
    },
    deleteItem: async (itemId) => {
        const res = await fetch(`${BASE}/items/${itemId}/`, {
            method: "DELETE", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to remove update");
    },

    // ── Publish ─────────────────────────────────────────
    publishRelease: async (releaseId, servers) => {
        const res = await fetch(`${BASE}/releases/${releaseId}/publish/`, {
            method: "POST", headers: getAuthHeaders(),
            body: JSON.stringify(servers ? { servers } : {}),
        });
        // 207 means some deployments took it and some did not. That is NOT an
        // error — the successful ones really did receive it — so it is returned
        // for the caller to report per server rather than thrown.
        const data = await res.json().catch(() => ({}));
        if (!res.ok && res.status !== 207) {
            const err = new Error(data.message || "Failed to publish");
            err.payload = data;
            throw err;
        }
        return data;
    },
    unpublishRelease: async (releaseId) => {
        const res = await fetch(`${BASE}/releases/${releaseId}/publish/`, {
            method: "DELETE", headers: getAuthHeaders(), body: JSON.stringify({}),
        });
        return handle(res, "Failed to retract release");
    },

    // ── Read receipts ───────────────────────────────────
    getReceipts: async (releaseId) => {
        const res = await fetch(`${BASE}/releases/${releaseId}/receipts/`, {
            method: "GET", headers: getAuthHeaders(),
        });
        return handle(res, "Failed to load read receipts");
    },
    refreshReceipts: async (releaseId) => {
        const res = await fetch(`${BASE}/releases/${releaseId}/receipts/`, {
            method: "POST", headers: getAuthHeaders(), body: JSON.stringify({}),
        });
        return handle(res, "Failed to refresh read receipts");
    },
};

/* ── display helpers ─────────────────────────────────────────────────────── */

export const STATUS_CHIP = {
    draft: { label: "Draft", cls: "wn-chip wn-chip--draft" },
    published: { label: "Published", cls: "wn-chip wn-chip--published" },
    archived: { label: "Retracted", cls: "wn-chip wn-chip--archived" },
};

export const PUSH_CHIP = {
    pending: { label: "Not sent", cls: "wn-chip wn-chip--pending" },
    pushed: { label: "Sent", cls: "wn-chip wn-chip--published" },
    failed: { label: "Failed", cls: "wn-chip wn-chip--failed" },
};

export const CATEGORY_VALUES = [
    { value: "feature", label: "New Feature" },
    { value: "enhancement", label: "Improvement" },
    { value: "bugfix", label: "Bug Fix" },
    { value: "configuration", label: "Configuration" },
];

export const fmtDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

/** A target key that is unique across deployments. Never org_id alone. */
export const orgKey = (serverCode, orgId) => `${serverCode}:${orgId}`;
